import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { NOTE_CATEGORIES } from "../lib/content";
import MarketNoteCard from "../components/site/MarketNoteCard";
import SkylineDivider from "../components/site/SkylineDivider";
import ServiceMarker from "../components/site/ServiceMarker";

export default function MarketNotes() {
  const [notes, setNotes] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/market-notes", {
        params: category === "All" ? {} : { category },
      })
      .then((r) => setNotes(r.data || []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div>
      {/* HERO with skyline depth */}
      <section
        data-testid="notes-hero"
        className="bg-obsidian text-paper py-28 md:py-40 relative overflow-hidden"
      >
        <div className="skyline"></div>
        <div className="skyline-sunrise"></div>
        <div className="ambient-glow"></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <div className="mb-10"><ServiceMarker name="editorial-mark" /></div>
          <span className="eyebrow">Market Notes · Intelligence Journal</span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-[72px] leading-[1.02] mt-8 max-w-4xl">
            Signals from <span className="italic text-gold">the Kingdom.</span>
          </h1>
          <p className="mt-10 text-lg md:text-xl text-warm/85 leading-relaxed max-w-3xl font-light">
            Practical notes on Saudi market entry, commercial timing, buyer
            behaviour, setup decisions, hiring, and the operating realities
            international companies need to understand before they move.
          </p>
        </div>
        <SkylineDivider variant="gold" opacity={0.22} height={160} className="skyline-bottom" />
      </section>

      {/* LISTING */}
      <section data-testid="notes-listing" className="bg-cream py-24 md:py-32 relative overflow-hidden">
        <div className="najdi-bg" style={{ opacity: 0.04 }}></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <div className="mb-12">
            <span className="eyebrow eyebrow-dark">Filter by category</span>
          </div>
          <div className="flex flex-wrap gap-3 mb-20">
            {NOTE_CATEGORIES.map((c) => {
              const active = category === c;
              return (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-6 py-3 text-xs uppercase tracking-[0.2em] border transition-all duration-500 flex items-center gap-2.5 ${
                    active
                      ? "bg-kingdom text-paper border-kingdom"
                      : "bg-transparent text-charcoal/70 border-charcoal/20 hover:border-gold hover:text-charcoal"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full transition-colors duration-500 ${
                      active ? "bg-gold" : "bg-charcoal/25"
                    }`}
                  ></span>
                  {c}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="bg-white border border-charcoal/10 min-h-[300px] animate-pulse"
                ></div>
              ))}
            </div>
          ) : notes.length === 0 ? (
            <p className="text-charcoal/60 italic text-center py-24 font-display text-lg">
              No notes in this category yet.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {notes.map((n) => (
                <MarketNoteCard key={n.slug} note={n} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
